import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DeleteModal } from '@common/components';
import TableWithPagination from '@common/components/TableWithPagination';
import Toolbar from '@common/components/Toolbar';
import { createEmptyFilter } from '@common/utils/filterUtils';
import useAsyncFilterOptions from '@common/hooks/useAsyncFilterOptions';
import {
  searchScheduledReports,
  bulkDeleteScheduledReports,
  updateScheduledReport,
  getScheduledReportFilterOptions,
  convertScheduledReportFiltersToApiFormat,
} from '@api/scheduledReports';
import { createColumns } from './tableColumns';
import SelectedScheduledReportsBar from './SelectedScheduledReportsBar/SelectedScheduledReportsBar';
import SaveScheduledReportModal from '../SaveScheduledReportModal/SaveScheduledReportModal';
import * as styles from './styles.module.css';

// Status constants
const STATUS_ENABLED = 1;
const STATUS_DISABLED = 0;

/**
 * Filter types for scheduled reports
 * isAsync: true - options loaded from API, false - static options
 */
const SCHEDULED_REPORTS_FILTER_TYPES = [
  { value: 'name', label: 'Name', isAsync: true },
  { value: 'description', label: 'Description', isAsync: true },
  { value: 'scheduleType', label: 'Schedule Type', isAsync: true },
  { value: 'outputFormat', label: 'Output Format', isAsync: true },
  { value: 'status', label: 'Status', isAsync: false },
];

/**
 * Get translated label for a filter type
 * @param {string} filterValue - The filter type value
 * @param {function} t - Translation function
 * @returns {string} - Translated label
 */
const getTranslatedFilterLabel = (filterValue, t) => {
  switch (filterValue) {
    case 'name':
      return t('common.name');
    case 'description':
      return t('common.description');
    case 'scheduleType':
      return t('scheduledReports.filters.scheduleType');
    case 'outputFormat':
      return t('scheduledReports.filters.outputFormat');
    case 'status':
      return t('scheduledReports.filters.status');
    default:
      return filterValue;
  }
};

/**
 * Get filter types with translated labels
 */
const getFilterTypes = (t) =>
  SCHEDULED_REPORTS_FILTER_TYPES.map((type) => ({
    ...type,
    label: getTranslatedFilterLabel(type.value, t),
  }));

/**
 * Get filter values with translated labels
 */
const getFilterValues = (t) => ({
  status: [
    { value: '1', label: t('scheduledReports.status.enabled') },
    { value: '0', label: t('scheduledReports.status.disabled') },
  ],
});

/**
 * Check if a filter type is async (loads options from API)
 */
const isAsyncFilterType = (filterType) => {
  const type = SCHEDULED_REPORTS_FILTER_TYPES.find(
    (ft) => ft.value === filterType,
  );
  return type?.isAsync ?? false;
};

/**
 * Build filters for API request
 */
const buildScheduledReportFilters = (filters) => {
  const apiFilters = [];

  filters.forEach((filter) => {
    if (filter.type && filter.value) {
      // For multi-select filters, use the first value
      const value = Array.isArray(filter.value)
        ? filter.value[0]
        : filter.value;
      if (value) {
        apiFilters.push({
          type: filter.type,
          value,
        });
      }
    }
  });

  return apiFilters;
};

/**
 * ScheduledReportsTableView Component
 *
 * Displays a table of scheduled reports with search, filtering, and actions
 */
export default function ScheduledReportsTableView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedRowIds, setSelectedRowIds] = useState({});
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([createEmptyFilter()]);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [deselectedRowIds, setDeselectedRowIds] = useState({});

  // Edit name/format modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingScheduledReport, setEditingScheduledReport] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('idle');
  const [deleteResults, setDeleteResults] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [selectedScheduledReport, setSelectedScheduledReport] = useState(null);

  const getRowId = useCallback((row) => String(row.id), []);

  // Use react-query for data fetching with proper loading/error states
  const {
    data: queryData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: [
      'scheduledReports',
      pageIndex,
      pageSize,
      sorting,
      searchTerm,
      filters,
    ],
    queryFn: async () => {
      const apiFilters = buildScheduledReportFilters(filters);
      const result = await searchScheduledReports({
        page: pageIndex,
        pageSize,
        sortField: sorting[0]?.id || 'createdAt',
        sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
        filters: apiFilters,
        searchTerm,
      });
      return result;
    },
  });

  const data = useMemo(() => queryData?.rows || [], [queryData?.rows]);
  const totalCount = queryData?.total || 0;

  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
  }, [queryClient]);

  useEffect(() => {
    setPageIndex(1);
  }, [sorting, searchTerm, filters]);

  // Handle adding a new scheduled report
  const handleAddScheduledReport = useCallback(() => {
    navigate('/schedule/new');
  }, [navigate]);

  // Handle editing name and format
  const handleEditNameFormat = useCallback((scheduledReport) => {
    setEditingScheduledReport(scheduledReport);
    setEditError(null);
    setEditModalOpen(true);
  }, []);

  // Handle saving edited name/format
  const handleSaveNameFormat = useCallback(
    async ({ name, description, outputFormat }) => {
      if (!editingScheduledReport) return;

      setEditSaving(true);
      setEditError(null);

      try {
        const result = await updateScheduledReport(editingScheduledReport.id, {
          name,
          description,
          outputFormat,
        });

        if (result.status === 'success') {
          setEditModalOpen(false);
          setEditingScheduledReport(null);
          refreshData();
        } else {
          setEditError(
            result.message || t('scheduledReports.errors.updateFailed'),
          );
        }
      } catch {
        setEditError(t('scheduledReports.errors.updateFailed'));
      } finally {
        setEditSaving(false);
      }
    },
    [editingScheduledReport, refreshData, t],
  );

  // Handle closing the edit modal
  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingScheduledReport(null);
    setEditError(null);
  }, []);

  // Handle modifying scheduled report (navigate to schedule page)
  const handleModifyScheduledReport = useCallback(
    (scheduledReport) => {
      navigate(`/schedule/${scheduledReport.id}/edit`, {
        state: {
          scheduledReport,
        },
      });
    },
    [navigate],
  );

  const handleEnable = useCallback(
    async (scheduledReport) => {
      try {
        const result = await updateScheduledReport(scheduledReport.id, {
          status: STATUS_ENABLED,
        });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to enable scheduled report:', error);
      }
    },
    [refreshData],
  );

  const handleDisable = useCallback(
    async (scheduledReport) => {
      try {
        const result = await updateScheduledReport(scheduledReport.id, {
          status: STATUS_DISABLED,
        });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to disable scheduled report:', error);
      }
    },
    [refreshData],
  );

  // Handler for inline status update via dropdown
  const handleUpdateStatus = useCallback(
    async (scheduledReport, newStatus) => {
      try {
        const result = await updateScheduledReport(scheduledReport.id, { status: newStatus });
        if (result.status === 'success') {
          refreshData();
        }
      } catch (error) {
        console.error('Failed to update scheduled report status:', error);
      }
    },
    [refreshData],
  );

  const handleDeselectAll = useCallback(() => {
    setSelectedRowIds({});
    setSelectAllMode(false);
    setDeselectedRowIds({});
  }, []);

  const handleDeleteClick = useCallback((scheduledReport) => {
    setSelectedScheduledReport(scheduledReport);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedScheduledReport(null);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
    setOpenAccordion(null);
  }, []);

  const handleDeleteSubmit = useCallback(async () => {
    setDeleteStatus('loading');
    setDeleteError(null);

    try {
      if (selectedScheduledReport) {
        // Single delete
        await bulkDeleteScheduledReports({ ids: [selectedScheduledReport.id] });
        setDeleteResults({
          success: [{ name: selectedScheduledReport.name }],
          failed: [],
        });
      } else {
        // Bulk delete
        const selectedIds = Object.keys(selectedRowIds)
          .filter((id) => selectedRowIds[id])
          .map((id) => Number(id));

        const result = await bulkDeleteScheduledReports({ ids: selectedIds });

        if (result.status === 'success') {
          // Get names of deleted reports from current data
          const deletedReports = data
            .filter((report) => selectedIds.includes(report.id))
            .map((report) => ({ name: report.name }));

          setDeleteResults({
            success: deletedReports,
            failed: [],
          });
        } else {
          setDeleteError(result.message || t('scheduledReports.deleteConfirm.errorTitle'));
          setDeleteStatus('error');
          return;
        }
      }

      setDeleteStatus('results');
      refreshData();
      handleDeselectAll();
    } catch (error) {
      setDeleteStatus('error');
      setDeleteError(error.message || t('scheduledReports.deleteConfirm.errorTitle'));
    }
  }, [selectedScheduledReport, selectedRowIds, data, refreshData, handleDeselectAll, t]);

  const handleBulkDelete = useCallback(() => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    setSelectedScheduledReport(null);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, [selectedRowIds]);

  const handleBulkEnable = useCallback(async () => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          updateScheduledReport(id, { status: STATUS_ENABLED }),
        ),
      );
      setSelectedRowIds({});
      setSelectAllMode(false);
      setDeselectedRowIds({});
      refreshData();
    } catch (error) {
      console.error('Failed to enable scheduled reports:', error);
    }
  }, [selectedRowIds, refreshData]);

  const handleBulkDisable = useCallback(async () => {
    const selectedIds = Object.keys(selectedRowIds)
      .filter((id) => selectedRowIds[id])
      .map((id) => Number(id));

    if (selectedIds.length === 0) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          updateScheduledReport(id, { status: STATUS_DISABLED }),
        ),
      );
      setSelectedRowIds({});
      setSelectAllMode(false);
      setDeselectedRowIds({});
      refreshData();
    } catch (error) {
      console.error('Failed to disable scheduled reports:', error);
    }
  }, [selectedRowIds, refreshData]);

  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSearchRemove = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(1);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setPageIndex((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil(totalCount / pageSize);
    setPageIndex((prev) => Math.min(maxPage, prev + 1));
  }, [totalCount, pageSize]);

  const canPreviousPage = pageIndex > 1;
  const canNextPage = pageIndex < Math.ceil(totalCount / pageSize);

  const columns = useMemo(
    () =>
      createColumns(t, {
        onEditNameFormat: handleEditNameFormat,
        onModifyScheduledReport: handleModifyScheduledReport,
        onEnable: handleEnable,
        onDisable: handleDisable,
        onDelete: handleDeleteClick,
        onUpdateStatus: handleUpdateStatus,
      }),
    [
      t,
      handleEditNameFormat,
      handleModifyScheduledReport,
      handleEnable,
      handleDisable,
      handleDeleteClick,
      handleUpdateStatus,
    ],
  );

  const filterTypes = useMemo(() => getFilterTypes(t), [t]);
  const filterValues = useMemo(() => getFilterValues(t), [t]);

  // Use shared hook for async filter options with caching
  const { loadFilterOptions, handleFilterDropdownOpen } = useAsyncFilterOptions(
    {
      filters,
      fetchFilterOptions: getScheduledReportFilterOptions,
      convertFiltersToApiFormat: convertScheduledReportFiltersToApiFormat,
      isAsyncFilterType,
      staticFilterValues: filterValues,
    },
  );

  const selectedCount = useMemo(() => {
    if (selectAllMode) {
      const deselectedCount =
        Object.values(deselectedRowIds).filter(Boolean).length;
      return totalCount - deselectedCount;
    }
    return Object.values(selectedRowIds).filter(Boolean).length;
  }, [selectAllMode, totalCount, selectedRowIds, deselectedRowIds]);

  // Determine empty state translation key based on whether filters are active
  const hasActiveFilters = searchTerm || filters.some((f) => f.type && f.value);
  const emptyStateKey = hasActiveFilters
    ? 'common.noResults'
    : 'scheduledReports.noScheduledReports';

  // Create button config for toolbar
  const createButton = {
    label: t('scheduledReports.addScheduledReport'),
    onClick: handleAddScheduledReport,
  };

  return (
    <div className={styles['scheduled-reports-container']}>
      <div className={styles['table-wrapper']}>
        <Toolbar
          onSearchChange={handleSearchChange}
          onFiltersChange={handleFiltersChange}
          searchTerm={searchTerm}
          onSearchRemove={handleSearchRemove}
          filters={filters}
          filterTypes={filterTypes}
          filterValues={filterValues}
          loadFilterOptions={loadFilterOptions}
          onFilterDropdownOpen={handleFilterDropdownOpen}
          createButton={createButton}
        />
        <TableWithPagination
          isLoading={isLoading}
          error={queryError?.message}
          data={data}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          selectedRowIds={selectedRowIds}
          onSelectionChange={setSelectedRowIds}
          selectAllMode={selectAllMode}
          onSelectAllModeChange={setSelectAllMode}
          deselectedRowIds={deselectedRowIds}
          onDeselectedRowIdsChange={setDeselectedRowIds}
          totalCount={totalCount}
          columnSizing={columnSizing}
          onColumnSizingChange={setColumnSizing}
          columnSizingInfo={columnSizingInfo}
          onColumnSizingInfoChange={setColumnSizingInfo}
          getRowId={getRowId}
          emptyStateTranslationKey={emptyStateKey}
          pageIndex={pageIndex}
          pageSize={pageSize}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          onPageSizeChange={handlePageSizeChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />
      </div>
      {selectedCount > 0 && (
        <SelectedScheduledReportsBar
          numberOfSelected={selectedCount}
          onDeselectAll={handleDeselectAll}
          onEnable={handleBulkEnable}
          onDisable={handleBulkDisable}
          onDelete={handleBulkDelete}
        />
      )}

      {/* Edit Name & Format Modal */}
      <SaveScheduledReportModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveNameFormat}
        saving={editSaving}
        error={editError}
        defaultName={editingScheduledReport?.name || ''}
        defaultDescription={editingScheduledReport?.description || ''}
        defaultOutputFormat={editingScheduledReport?.outputFormat || 'pdf'}
        isEditMode
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        deleteStatus={deleteStatus}
        deleteResults={deleteResults}
        deleteError={deleteError}
        openAccordion={openAccordion}
        setOpenAccordion={setOpenAccordion}
        selectedCount={selectedScheduledReport ? 1 : selectedCount}
        onSubmit={handleDeleteSubmit}
        onClose={handleCloseDeleteModal}
        translationPrefix="scheduledReports.deleteConfirm"
        getItemName={(item) => item.name}
      />
    </div>
  );
}
